import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Chip,
} from '@mui/material';
import HandshakeIcon from '@mui/icons-material/Handshake';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useInventory } from '../../../contexts/InventoryContext';
import ProjectsToolbar from './ProjectsToolbar';
import ProjectListView from './ProjectListView';
import AddNewProject from './AddNewProject';
import CSVImportProjects from '../components/CSVImportProjects';

// ── Construction status config ────────────────────────────────────────────────
const STATUS_CONFIG = {
  'Pre-Launch':         { color: '#6a1b9a', bg: 'rgba(106,27,154,0.12)', dot: '#9c27b0' },
  'New Launch':         { color: '#0277bd', bg: 'rgba(2,119,189,0.12)',  dot: '#0288d1' },
  'Under Construction': { color: '#e65100', bg: 'rgba(230,81,0,0.12)',   dot: '#ff6d00' },
  'Near Possession':    { color: '#f57c00', bg: 'rgba(245,124,0,0.12)',  dot: '#ffa726' },
  'Ready to Move':      { color: '#2e7d32', bg: 'rgba(46,125,50,0.12)', dot: '#43a047' },
  'Ready To Move':      { color: '#2e7d32', bg: 'rgba(46,125,50,0.12)', dot: '#43a047' },
  'Completed':          { color: '#1b5e20', bg: 'rgba(27,94,32,0.12)',  dot: '#2e7d32' },
};
const getStatusConfig = (s) => STATUS_CONFIG[s] || { color: '#546e7a', bg: 'rgba(84,110,122,0.12)', dot: '#78909c' };

// ── Tag styles ────────────────────────────────────────────────────────────────
const TAG_STYLES = {
  Residential: { bg: 'rgba(25,118,210,0.10)', color: '#1565c0' },
  Commercial:  { bg: 'rgba(230,81,0,0.10)',   color: '#bf360c' },
  Retail:      { bg: 'rgba(156,39,176,0.10)', color: '#6a1b9a' },
  Luxury:      { bg: 'rgba(183,28,28,0.10)',  color: '#b71c1c' },
  Plots:       { bg: 'rgba(46,125,50,0.10)',  color: '#2e7d32' },
  Plot:        { bg: 'rgba(46,125,50,0.10)',  color: '#2e7d32' },
  Farmhouse:   { bg: 'rgba(121,85,72,0.12)',  color: '#5d4037' },
  Warehouse:   { bg: 'rgba(69,90,100,0.12)',  color: '#455a64' },
  'Farm Land': { bg: 'rgba(139,195,74,0.12)', color: '#558b2f' },
  'New Launch':{ bg: 'rgba(2,136,209,0.10)',  color: '#0277bd' },
};
const getTagStyle = (label) => TAG_STYLES[label] || { bg: 'rgba(97,97,97,0.10)', color: '#546e7a' };

// ── Default thumbnail when no project image and no developer logo ───────
const DEFAULT_THUMBNAIL = 'https://images.unsplash.com/photo-1613977257363-707ba9348227?w=600&h=280&fit=crop&q=85';

const getDeveloperLogo = (project, developers) => {
  if (!project?.developer || !Array.isArray(developers) || developers.length === 0) return null;
  const id = project.developer;
  const dev = developers.find((d) => d.id === id || d.id == id);
  return dev?.logo && typeof dev.logo === 'string' && dev.logo.startsWith('http') ? dev.logo : null;
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const getUnitStats = (p) => {
  const u = p?.unit_stats;
  if (u && typeof u === 'object') return u;
  return { available: p?.avail ?? p?.available ?? 0, booked: p?.booked ?? 0, hold: p?.hold ?? 0, sold: p?.sold ?? 0, total: p?.total ?? 0 };
};
const getProjectName = (p) => p?.project_name ?? p?.name ?? '';
const getConstructionStatus = (p) => p?.construction_status ?? p?.status ?? '';

const parseBudgetRangeToLakh = (str) => {
  if (!str || typeof str !== 'string') return { minLakh: null, maxLakh: null };
  const s = str.replace(/\s+/g, ' ').trim();
  const plusMatch = s.match(/₹\s*([\d.]+)\s*Cr\s*\+/i);
  if (plusMatch) return { minLakh: parseFloat(plusMatch[1]) * 100, maxLakh: null };
  const parts = s.split(/[–\-]/).map((x) => x.trim());
  const values = [];
  for (const part of parts) {
    const crMatch = part.match(/₹\s*([\d.]+)\s*Cr/i);
    const lMatch  = part.match(/₹\s*([\d.]+)\s*L/i);
    if (crMatch) values.push(parseFloat(crMatch[1]) * 100);
    else if (lMatch) values.push(parseFloat(lMatch[1]));
  }
  return {
    minLakh: values.length > 0 ? Math.min(...values) : null,
    maxLakh: values.length > 0 ? Math.max(...values) : null,
  };
};

const formatCommission = (val) => {
  if (val == null || val === '') return '—';
  const num = parseFloat(String(val).replace(/%/g, '').trim());
  return isNaN(num) ? '—' : `${num.toFixed(1)}%`;
};

const SCROLL_LOAD_THRESHOLD = 400;

// ── Project Card ──────────────────────────────────────────────────────────────
const ProjectCard = ({ p, onClick, developers = [] }) => {
  const [imgError, setImgError] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const status    = getConstructionStatus(p);
  const statusCfg = getStatusConfig(status);
  const tagList   = Array.isArray(p.tags) && p.tags.length > 0 ? p.tags : ['Residential'];
  const commission = formatCommission(p.commission ?? p.commission_rate);
  const budget = p.budget_range ?? p.budget;
  const developerLogo = getDeveloperLogo(p, developers);
  const thumbnail = (!imgError && p.thumbnail)
    ? p.thumbnail
    : ((!logoError && developerLogo) ? developerLogo : DEFAULT_THUMBNAIL);
  const isDeveloperLogo = thumbnail === developerLogo;

  const handleImgError = () => {
    if (thumbnail === developerLogo) setLogoError(true);
    else setImgError(true);
  };

  return (
    <Box
      onClick={onClick}
      sx={{
        borderRadius: 2.5,
        overflow: 'hidden',
        bgcolor: 'white',
        border: '1px solid',
        borderColor: 'rgba(0,0,0,0.08)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.22s ease',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
          borderColor: 'rgba(0,0,0,0.13)',
          '& .card-arrow': { opacity: 1, transform: 'translateX(3px)' },
          '& .card-img': { transform: isDeveloperLogo ? 'none' : 'scale(1.04)' },
        },
      }}
    >
      {/* ── Image header (project thumbnail, or developer logo, or default) ───── */}
      <Box sx={{ position: 'relative', height: 148, overflow: 'hidden', bgcolor: '#e8eff5', flexShrink: 0 }}>
        <Box
          className="card-img"
          component="img"
          src={thumbnail}
          alt={getProjectName(p)}
          onError={handleImgError}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: isDeveloperLogo ? 'contain' : 'cover',
            transition: 'transform 0.4s ease',
            ...(isDeveloperLogo && { p: 1.5, boxSizing: 'border-box' }),
          }}
        />
        {/* Gradient overlay bottom */}
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55) 100%)',
        }} />

        {/* Status badge — top left */}
        {status && (
          <Box sx={{
            position: 'absolute', top: 10, left: 10,
            px: 1, py: 0.3,
            borderRadius: 10,
            bgcolor: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', gap: 0.5,
          }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: statusCfg.dot, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: 'white', letterSpacing: 0.3 }}>
              {status}
            </Typography>
          </Box>
        )}

        {/* Commission badge — top right */}
        <Box sx={{
          position: 'absolute', top: 10, right: 10,
          pl: 0.75, pr: 1, py: 0.3,
          borderRadius: 10,
          bgcolor: 'rgba(0,0,0,0.58)',
          backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', gap: 0.5,
          border: '1px solid rgba(255,255,255,0.15)',
        }}>
          <HandshakeIcon sx={{ fontSize: 11, color: '#69f0ae' }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
            <Typography sx={{ fontSize: 7.5, fontWeight: 600, color: 'rgba(255,255,255,0.65)', letterSpacing: 0.4, textTransform: 'uppercase', lineHeight: 1.1 }}>
              Commission
            </Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 900, color: '#69f0ae', letterSpacing: 0.2, lineHeight: 1.2 }}>
              {commission}
            </Typography>
          </Box>
        </Box>

        {/* Project name overlay on image bottom */}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, px: 1.5, pb: 1.25 }}>
          <Typography sx={{
            fontSize: 14, fontWeight: 800, color: 'white',
            lineHeight: 1.25, letterSpacing: -0.2,
            textShadow: '0 1px 4px rgba(0,0,0,0.4)',
            display: '-webkit-box', WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {getProjectName(p)}
          </Typography>
        </Box>
      </Box>

      {/* ── Card body ──────────────────────────────────────────────────── */}
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>

        {/* Location */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <LocationOnIcon sx={{ fontSize: 12, color: '#e53935', flexShrink: 0 }} />
          <Typography sx={{
            fontSize: 11, color: '#555', fontWeight: 500,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            fontStyle: p.location?.trim() ? 'normal' : 'italic',
          }}>
            {p.location?.trim() || 'Location not set'}
          </Typography>
        </Box>

        {/* Developer + Budget row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Box sx={{
              width: 20, height: 20, borderRadius: 1, flexShrink: 0,
              bgcolor: 'rgba(46,125,50,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <BusinessIcon sx={{ fontSize: 11, color: '#2e7d32' }} />
            </Box>
            <Typography sx={{
              fontSize: 11, fontWeight: 600, color: '#333',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {p.developer || '—'}
            </Typography>
          </Box>

          {budget && (
            <Typography sx={{
              fontSize: 11, fontWeight: 700, color: '#bf360c',
              whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              {budget}
            </Typography>
          )}
        </Box>

        {/* Divider */}
        <Box sx={{ height: '1px', bgcolor: 'rgba(0,0,0,0.06)' }} />

        {/* Tags + arrow */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1, minWidth: 0 }}>
            {tagList.slice(0, 3).map((tag) => {
              const ts = getTagStyle(tag);
              return (
                <Box key={tag} sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.4,
                  px: 0.75, py: 0.2, borderRadius: 10,
                  bgcolor: ts.bg,
                }}>
                  <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: ts.color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 9.5, fontWeight: 600, color: ts.color }}>
                    {tag}
                  </Typography>
                </Box>
              );
            })}
            {tagList.length > 3 && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 0.75, py: 0.2, borderRadius: 10, bgcolor: 'rgba(0,0,0,0.06)' }}>
                <Typography sx={{ fontSize: 9.5, fontWeight: 600, color: '#777' }}>+{tagList.length - 3}</Typography>
              </Box>
            )}
          </Box>
          <ChevronRightIcon
            className="card-arrow"
            sx={{ fontSize: 16, color: '#bbb', flexShrink: 0, opacity: 0.5, transition: 'all 0.2s ease' }}
          />
        </Box>
      </Box>
    </Box>
  );
};

// ─── Main View ────────────────────────────────────────────────────────────────
const ProjectGridView = () => {
  const {
    projects = [],
    setProjects,
    setDevelopers,
    fetchProjectsPage,
    refetchProjects,
    projectsLoading,
    developers = [],
    createProject,
    createDeveloper,
    inventoryOptions = {},
  } = useInventory();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const developerParam = searchParams.get('developer');
  const [builderFilter, setBuilderFilter] = useState(() => developerParam ?? '');
  const [viewMode, setViewMode] = useState('grid');
  const [search, setSearch] = useState('');
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [category, setCategory] = useState('All');
  const [constructionStatus, setConstructionStatus] = useState('All');
  const [nextPage, setNextPage] = useState(2);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreRef = useRef(false);
  const [newProjectDrawerOpen, setNewProjectDrawerOpen] = useState(false);
  const [importCSVDrawerOpen, setImportCSVDrawerOpen] = useState(false);

  // Sync builder filter from URL (e.g. browser back or link with ?developer=)
  useEffect(() => {
    const d = searchParams.get('developer');
    if (d !== builderFilter) setBuilderFilter(d ?? '');
  }, [searchParams]);

  // When URL has ?developer=, refetch projects for that builder and reset pagination
  useEffect(() => {
    const refetch = developerParam != null && developerParam !== '' ? refetchProjects(developerParam) : refetchProjects(null);
    refetch.then(() => {
      setNextPage(2);
      setHasMore(true);
    });
  }, [developerParam, refetchProjects]);

  const handleBuilderChange = useCallback((value) => {
    setBuilderFilter(value);
    if (value) setSearchParams({ developer: value }, { replace: true });
    else setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const loadMore = useCallback(() => {
    if (loadMoreRef.current || !hasMore || loadingMore || typeof fetchProjectsPage !== 'function') return;
    const setProjectsSafe = typeof setProjects === 'function' ? setProjects : () => {};
    loadMoreRef.current = true;
    setLoadingMore(true);
    const developerId = builderFilter && builderFilter !== '' ? builderFilter : null;
    fetchProjectsPage(nextPage, undefined, developerId)
      .then((out) => {
        const { results = [], hasMore: more = false } = out ?? {};
        setProjectsSafe((prev) => [...(Array.isArray(prev) ? prev : []), ...(Array.isArray(results) ? results : [])]);
        setNextPage((p) => p + 1);
        setHasMore(Boolean(more));
      })
      .finally(() => {
        setLoadingMore(false);
        loadMoreRef.current = false;
      });
  }, [nextPage, hasMore, loadingMore, fetchProjectsPage, setProjects, builderFilter]);

  useEffect(() => {
    const onScroll = () => {
      const bottom = document.documentElement.scrollHeight - window.scrollY - window.innerHeight;
      if (bottom < SCROLL_LOAD_THRESHOLD) loadMore();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [loadMore]);

  const filtered = useMemo(() => {
    const list = Array.isArray(projects) ? projects : [];
    return list.filter((p) => {
      const name   = getProjectName(p);
      const status = getConstructionStatus(p);
      const matchSearch = !search || [name, p.developer, p.location].some(
        (s) => s && String(s).toLowerCase().includes(search.toLowerCase())
      );
      const minVal = budgetMin === '' ? null : Number(budgetMin);
      const maxVal = budgetMax === '' ? null : Number(budgetMax);
      let matchBudget = true;
      if (minVal != null || maxVal != null) {
        const { minLakh: pMin, maxLakh: pMax } = parseBudgetRangeToLakh(p.budget_range ?? p.budget);
        if (pMin != null || pMax != null) {
          const projMin = pMin ?? 0, projMax = pMax ?? 1e9;
          const userMin = minVal ?? 0, userMax = maxVal ?? 1e9;
          matchBudget = projMax >= userMin && projMin <= userMax;
        }
      }
      const matchLocation = !locationFilter || (p.location && p.location.toLowerCase().includes(locationFilter.toLowerCase()));
      const matchCategory = category === 'All' || (p.tags && Array.isArray(p.tags) && p.tags.includes(category)) || p.segment === category || p.category === category;
      const matchStatus   = constructionStatus === 'All' || (status && status === constructionStatus);
      return matchSearch && matchBudget && matchLocation && matchCategory && matchStatus;
    });
  }, [projects, search, budgetMin, budgetMax, locationFilter, category, constructionStatus]);

  const constructionStatusOpts = useMemo(() => {
    const arr = inventoryOptions?.construction_statuses;
    if (!Array.isArray(arr) || arr.length === 0) return undefined;
    return ['All', ...arr.map((s) => (typeof s === 'object' && s != null && 'label' in s ? s.label : String(s)))];
  }, [inventoryOptions?.construction_statuses]);

  const categoryOpts = useMemo(() => {
    const arr = inventoryOptions?.categories;
    if (!Array.isArray(arr) || arr.length === 0) return undefined;
    return ['All', ...arr.map((c) => (typeof c === 'object' && c != null && 'label' in c ? c.label : String(c)))];
  }, [inventoryOptions?.categories]);

  const statusCounts = useMemo(() => ({
    under:  filtered.filter((p) => getConstructionStatus(p) === 'Under Construction').length,
    launch: filtered.filter((p) => getConstructionStatus(p) === 'New Launch').length,
    ready:  filtered.filter((p) => ['Ready to Move', 'Ready To Move'].includes(getConstructionStatus(p))).length,
  }), [filtered]);

  const handleProjectClick = (project) => navigate(`/inventory/projects/${project.id}/`);

  if (projectsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 280, p: 3 }}>
        <CircularProgress size={40} sx={{ color: 'success.main' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, width: '100%', alignSelf: 'flex-start' }}>
      {/* Header row */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            Showing <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{filtered.length}</Box> projects
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {[
              { color: '#ff6d00', label: `Under Construction: ${statusCounts.under}` },
              { color: '#0288d1', label: `New Launch: ${statusCounts.launch}` },
              { color: '#43a047', label: `Ready to Move: ${statusCounts.ready}` },
            ].map(({ color, label }) => (
              <Chip
                key={label}
                size="small"
                label={label.split(':')[0]}
                sx={{
                  height: 20, fontSize: 9.5, fontWeight: 700,
                  bgcolor: `${color}18`, color, border: `1px solid ${color}30`,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            ))}
          </Box>
        </Box>
        <ToggleButtonGroup
          value={viewMode} exclusive
          onChange={(_, v) => v != null && setViewMode(v)}
          size="small"
        >
          <ToggleButton value="grid"><GridViewIcon /></ToggleButton>
          <ToggleButton value="list"><ViewListIcon /></ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <ProjectsToolbar
        search={search} onSearchChange={setSearch}
        budgetMin={budgetMin} budgetMax={budgetMax}
        onBudgetMinChange={setBudgetMin} onBudgetMaxChange={setBudgetMax}
        locationFilter={locationFilter} onLocationChange={setLocationFilter}
        constructionStatus={constructionStatus} onConstructionStatusChange={setConstructionStatus}
        category={category} onCategoryChange={setCategory}
        builderFilter={builderFilter}
        onBuilderChange={handleBuilderChange}
        builderOptions={developers}
        onImport={() => setImportCSVDrawerOpen(true)}
        onNewProject={() => setNewProjectDrawerOpen(true)}
        constructionStatusOptions={constructionStatusOpts}
        categoryOptions={categoryOpts}
      />

      {/* Grid / List */}
      {viewMode === 'list' ? (
        <ProjectListView
          projects={filtered} onProjectClick={handleProjectClick}
          getProjectName={getProjectName} getUnitStats={getUnitStats}
          getConstructionStatus={getConstructionStatus}
          loadMore={loadMore} loadingMore={loadingMore} hasMore={hasMore}
        />
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(3,1fr)', lg: 'repeat(4,1fr)' },
          gap: 2,
          alignContent: 'start',
          alignItems: 'start',
        }}>
          {filtered.map((p, idx) => (
            <ProjectCard key={p?.id ?? `project-${idx}`} p={p} developers={developers} onClick={() => handleProjectClick(p)} />
          ))}
        </Box>
      )}

      {/* Load more */}
      {filtered.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          {loadingMore && <CircularProgress size={32} />}
          {!loadingMore && hasMore && (
            <Typography variant="caption" color="text.secondary">Scroll down to load more</Typography>
          )}
        </Box>
      )}

      {filtered.length === 0 && (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">No projects match your filters.</Typography>
        </Box>
      )}

      {/* Drawers */}
      <AddNewProject
        open={newProjectDrawerOpen}
        onClose={() => setNewProjectDrawerOpen(false)}
        initialDeveloper={
          developerParam && developers?.length
            ? developers.find((d) => String(d.id) === String(developerParam)) ?? null
            : null
        }
        onSubmit={async (data) => {
          if (!data || typeof createProject !== 'function') return;
          let developerId = data.developer_id != null ? (Number(data.developer_id) || data.developer_id) : null;
          if (!developerId && data.developer_name?.trim() && typeof createDeveloper === 'function') {
            try {
              const created = await createDeveloper({ name: data.developer_name.trim() });
              developerId = created?.id ?? null;
            } catch (_) {
              return;
            }
          }
          const payload = {
            name: (data.name && String(data.name).trim()) || '',
            developer: developerId,
            construction_status: data.status || 'under_construction',
            description: (data.description && String(data.description).trim()) || '',
            commission_rate: data.commission_rate != null ? parseFloat(data.commission_rate) : null,
            commission_type: data.commission_type || 'percentage',
            price_min: data.price_min ?? null,
            price_max: data.price_max ?? null,
            total_area: data.total_area != null && data.total_area !== '' ? parseFloat(data.total_area) : null,
            area_unit: data.area_unit || 'sqft',
            possession_date: data.possession_date || null,
            rera_no: (data.rera_no && String(data.rera_no).trim()) || null,
            tags: Array.isArray(data.tags) ? data.tags : [],
            metadata: data.metadata && typeof data.metadata === 'object' ? data.metadata : {},
          };
          if (data.address && typeof data.address === 'object') payload.address = data.address;
          try {
            await createProject(payload);
          } catch (_) {}
        }}
      />
    </Box>
  );
};

export default ProjectGridView;
